import {
    KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION,
    KERNEL_CANVAS_ARTIFACT_SCHEMA,
    assertValidKernelCanvasArtifact,
} from "./kernelCanvasArtifact.js";
import {
    BUILTIN_KERNEL_IDS,
    BUILTIN_KERNEL_PORTABLE_IDS,
    isPortableTenantViewId,
    portableTenantViewIdToWireId,
    wireTenantViewIdToPortableId,
} from "./builtins/bundledArtifactIds.js";
import {
    DEFAULT_SECRET_INVENTORY_LIMIT,
    inventoryDefinitionSecrets,
} from "./tenantViewSecretDetector.js";
const textClone = (value) => JSON.parse(JSON.stringify(value));
const builtinKernelIdMap = new Map(Object.keys(BUILTIN_KERNEL_PORTABLE_IDS).map((command) => [
    BUILTIN_KERNEL_PORTABLE_IDS[command],
    BUILTIN_KERNEL_IDS[command],
]));

function canonicalJsonValue(value) {
    if (value === null || typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) {
        return `[${value.map((entry) => canonicalJsonValue(entry)).join(",")}]`;
    }
    return `{${Object.keys(value).sort().map((key) =>
        `${JSON.stringify(key)}:${canonicalJsonValue(value[key])}`).join(",")}}`;
}

/**
 * Deterministic JSON bytes for reproducible downloads. Arrays
 * retain their validated semantic order; object keys are recursively sorted.
 */
export function canonicalTenantViewArtifactJson(artifact) {
    return `${canonicalJsonValue(artifact)}\n`;
}

function metadataOf(view) {
    return view?.metadata || view;
}

function bodyOf(view) {
    return view?.definition || view?.body || view?.definitionBody || null;
}

function kindOf(metadata) {
    const kind = String(metadata?.kind || "").toLowerCase();
    if (kind.endsWith("_kernel") || kind === "kernel" || kind === "1") return "kernel";
    if (kind.endsWith("_canvas") || kind === "canvas" || kind === "2") return "canvas";
    return "";
}

function wireOrBodyIdToUuid(id) {
    if (isPortableTenantViewId(id)) return id.toLowerCase();
    return wireTenantViewIdToPortableId(id);
}

function rowsOf(canvas) {
    if (Array.isArray(canvas?.rows)) return canvas.rows;
    if (Array.isArray(canvas?.items)) {
        return canvas.items.map((cell) => ({ cells: [{ ...cell }] }));
    }
    return [];
}

function kernelRefs(canvas) {
    const refs = new Set();
    for (const row of rowsOf(canvas)) {
        for (const cell of Array.isArray(row?.cells) ? row.cells : []) {
            const id = cell?.kernel_id ?? cell?.kernelId;
            if (typeof id !== "string") continue;
            refs.add(
                isPortableTenantViewId(id)
                    ? id.toLowerCase()
                    : wireTenantViewIdToPortableId(id),
            );
        }
    }
    return refs;
}

function edgeList(objects) {
    const edges = [];
    const seen = new Set();
    for (const object of objects) {
        if (object.kind !== "canvas") continue;
        for (const kernelId of kernelRefs(object)) {
            const key = `${object.id}:${kernelId}`;
            if (seen.has(key)) continue;
            seen.add(key);
            edges.push({ canvas_id: object.id, kernel_id: kernelId });
        }
    }
    return edges.sort((a, b) =>
        a.canvas_id.localeCompare(b.canvas_id) || a.kernel_id.localeCompare(b.kernel_id));
}

/**
 * Portable deployment/backup bundles have one root canvas and only its exact
 * dependency closure. This is stricter than the reusable structural schema.
 */
export function assertTenantViewPortableArtifactShape(artifact) {
    const objects = Array.isArray(artifact?.objects) ? artifact.objects : [];
    const canvases = objects.filter((object) => object?.kind === "canvas");
    const kernels = objects.filter((object) => object?.kind === "kernel");
    if (canvases.length !== 1) {
        throw new Error("portable artifact must contain exactly one canvas");
    }
    if (kernels.length > 20) {
        throw new Error("portable artifact must contain at most 20 kernels");
    }
    for (const object of objects) {
        if (typeof object?.title !== "string" || object.title.trim() === "") {
            throw new Error(`portable ${object?.kind || "object"} title must not be blank`);
        }
        const titleBytes = new TextEncoder().encode(object.title).byteLength;
        if (titleBytes > 255) {
            throw new Error(`portable ${object.kind} title is ${titleBytes} bytes; maximum is 255`);
        }
    }
    const referenced = kernelRefs(canvases[0]);
    const kernelIds = new Set(kernels.map((kernel) => kernel.id));
    const missing = [...referenced].find((id) => !kernelIds.has(id));
    if (missing) throw new Error(`portable artifact is missing referenced kernel ${missing}`);
    const extra = [...kernelIds].find((id) => !referenced.has(id));
    if (extra) throw new Error(`portable artifact contains unreferenced kernel ${extra}`);
    return artifact;
}

function portableObject(view) {
    const metadata = metadataOf(view);
    const body = bodyOf(view);
    if (!metadata || !body || typeof body !== "object") {
        throw new TypeError("tenant view body is required for export");
    }
    const kind = kindOf(metadata);
    if (!kind) throw new TypeError("tenant view kind is invalid");
    const definition = textClone(body);
    if (kind === "canvas") {
        definition.rows = rowsOf(definition).map((row) => ({
            ...row,
            cells: (row.cells || []).map((cell) => {
                const sourceId = cell?.kernel_id ?? cell?.kernelId;
                if (typeof sourceId !== "string" || !sourceId) return { ...cell };
                const next = {
                    ...cell,
                    kernel_id: wireOrBodyIdToUuid(sourceId),
                };
                delete next.kernelId;
                return next;
            }),
        }));
        delete definition.items;
    }
    return {
        ...definition,
        id: wireOrBodyIdToUuid(metadata.id),
        kind,
        ...(kind === "kernel" ? { command: metadata.name } : { slug: metadata.name }),
        title: metadata.title || body.title || metadata.name,
    };
}

function artifactLimits(capabilities = {}) {
    const limits = {
        maxObjects: 21,
        maxCanvases: 1,
        maxKernels: 20,
        maxArtifactBytes: 6 * 1024 * 1024,
    };
    if (Number.isFinite(capabilities.maxKernelSteps)) {
        limits.maxStepsPerKernel = capabilities.maxKernelSteps;
    }
    if (Number.isFinite(capabilities.maxCanvasCells)) {
        limits.maxCellsPerCanvas = capabilities.maxCanvasCells;
    }
    if (Number.isFinite(capabilities.maxCanvasRows)) {
        limits.maxRowsPerCanvas = capabilities.maxCanvasRows;
    }
    if (Number.isFinite(capabilities.maxDefinitionBytes)) {
        limits.maxDefinitionBytes = capabilities.maxDefinitionBytes;
    }
    if (Number.isFinite(capabilities.maxDefinitionDepth)) {
        limits.maxJsonDepth = capabilities.maxDefinitionDepth;
    }
    if (Number.isFinite(capabilities.maxPortableArtifactBytes)) {
        limits.maxArtifactBytes = capabilities.maxPortableArtifactBytes;
    }
    return limits;
}

function withSecretInventory(artifact, inventory = inventoryDefinitionSecrets) {
    const findings = [];
    let total = 0;
    let truncated = false;
    for (const object of artifact.objects) {
        const result = inventory(object, {
            limit: Math.max(0, DEFAULT_SECRET_INVENTORY_LIMIT - findings.length),
        });
        total += Number(result?.total || 0);
        truncated ||= Boolean(result?.truncated);
        for (const finding of result?.findings || []) {
            if (findings.length >= DEFAULT_SECRET_INVENTORY_LIMIT) {
                truncated = true;
                break;
            }
            findings.push({
                object_id: object.id,
                kind: String(finding.kind || "unknown"),
                json_pointer: String(finding.json_pointer || ""),
            });
        }
    }
    return {
        ...artifact,
        secret_findings: {
            findings,
            total,
            truncated: truncated || total > findings.length,
        },
    };
}

function pointerToken(value) {
    return String(value).replace(/~/g, "~0").replace(/\//g, "~1");
}

const SYMBOLIC_ENUM_VALUE = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+){2,}$/;

function identifierKind(value) {
    if (isPortableTenantViewId(value)) return "uuid";
    if (SYMBOLIC_ENUM_VALUE.test(value)) return null;
    if (/^[A-Za-z0-9+/_-]{22,}={0,2}$/.test(value)) {
        const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
        try {
            const decoded = atob(padded);
            if (decoded.length >= 16 && btoa(decoded) === padded) {
                return "base64";
            }
        } catch {
            // Not base64.
        }
    }
    if (/^[^@\s]+@[^@\s]+\.[A-Za-z]{2,}$/.test(value)) return "email";
    try {
        const url = new URL(value);
        if ((url.protocol === "http:" || url.protocol === "https:") && url.hostname.includes(".")) {
            return "hostname";
        }
    } catch {
        if (/^(?=.{1,253}$)(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.)+[A-Za-z]{2,63}$/.test(value)) {
            return "hostname";
        }
    }
    return null;
}

function isStructuralIdentifierPath(definition, path) {
    return path === "/id"
        || (
            definition?.kind === "canvas"
            && /^\/rows\/\d+\/cells\/\d+\/kernel_id$/.test(path)
        );
}

export function inventoryDefinitionIdentifiers(definition, { limit = 100 } = {}) {
    const maximum = Math.max(0, Math.floor(Number(limit) || 0));
    const findings = [];
    let total = 0;

    const visit = (value, path) => {
        if (typeof value === "string") {
            const kind = isStructuralIdentifierPath(definition, path)
                ? null
                : identifierKind(value);
            if (kind) {
                total += 1;
                if (findings.length < maximum) findings.push({ kind, json_pointer: path });
            }
            return;
        }
        if (value === null || typeof value !== "object") return;
        if (Array.isArray(value)) {
            value.forEach((entry, index) => visit(entry, `${path}/${index}`));
            return;
        }
        for (const key of Object.keys(value).sort()) {
            visit(value[key], `${path}/${pointerToken(key)}`);
        }
    };
    visit(definition, "");
    return { findings, total, truncated: total > findings.length };
}

function withIdentifierInventory(artifact, inventory = inventoryDefinitionIdentifiers) {
    const findings = [];
    let total = 0;
    let truncated = false;
    for (const object of artifact.objects) {
        const result = inventory(object, { limit: Math.max(0, 100 - findings.length) });
        total += Number(result?.total || 0);
        truncated ||= Boolean(result?.truncated);
        for (const finding of result?.findings || []) {
            if (findings.length >= 100) {
                truncated = true;
                break;
            }
            findings.push({
                object_id: object.id,
                kind: String(finding.kind || "unknown"),
                json_pointer: String(finding.json_pointer || ""),
            });
        }
    }
    return {
        ...artifact,
        identifier_findings: {
            findings,
            total,
            truncated: truncated || total > findings.length,
        },
    };
}

export function inventoryTenantViewArtifact(
    artifact,
    {
        inventorySecrets = inventoryDefinitionSecrets,
        inventoryIdentifiers = inventoryDefinitionIdentifiers,
    } = {},
) {
    const source = { ...artifact };
    delete source.identifier_findings;
    delete source.secret_findings;
    const inventoried = withIdentifierInventory(
        withSecretInventory(source, inventorySecrets),
        inventoryIdentifiers,
    );
    return {
        identifier_findings: inventoried.identifier_findings,
        secret_findings: inventoried.secret_findings,
    };
}

/**
 * Export roots selected by their REST/base64 id. Canvas dependencies are
 * closed over exact RFC-4122 ids from their definitions. Root objects must be
 * owned by the exporting viewer; readable dependency bodies may be included.
 */
export async function exportTenantViewArtifact({
    metadata,
    getView,
    viewerUserId,
    kernelIds = [],
    canvasIds = [],
    bundledArtifact = null,
    capabilities = {},
    inventorySecrets = inventoryDefinitionSecrets,
    inventoryIdentifiers = inventoryDefinitionIdentifiers,
}) {
    if (typeof getView !== "function") throw new TypeError("getView is required");
    const rows = Array.isArray(metadata) ? metadata : [];
    const byWireId = new Map(rows.map((row) => [row.id, row]));
    const byUuid = new Map(rows.map((row) => [wireOrBodyIdToUuid(row.id), row]));
    const selected = [...kernelIds, ...canvasIds];
    const queue = [];
    for (const id of selected) {
        const row = byWireId.get(id) || byUuid.get(wireOrBodyIdToUuid(id));
        if (!row) throw new Error(`tenant view ${id} is not readable`);
        if (viewerUserId && row.createdBy !== viewerUserId) {
            throw new Error(`tenant view ${id} is not owned by the current user`);
        }
        queue.push(row);
    }

    const bundledById = new Map(
        (bundledArtifact?.objects || []).map((object) => [object.id, textClone(object)]),
    );
    const objects = new Map();
    const fetched = new Map();
    while (queue.length) {
        const row = queue.shift();
        const uuid = wireOrBodyIdToUuid(row.id);
        if (objects.has(uuid)) continue;
        let view = fetched.get(row.id);
        if (!view) {
            view = await getView(row.id);
            fetched.set(row.id, view);
        }
        const object = portableObject(view);
        objects.set(uuid, object);
        if (object.kind !== "canvas") continue;
        for (const dependencyId of kernelRefs(object)) {
            if (objects.has(dependencyId)) continue;
            const dependency = byUuid.get(dependencyId);
            if (dependency) queue.push(dependency);
            else if (bundledById.has(dependencyId)) {
                objects.set(dependencyId, bundledById.get(dependencyId));
            } else {
                throw new Error(`canvas ${object.id} has unreadable dependency ${dependencyId}`);
            }
        }
    }

    const portableObjects = [...objects.values()];
    const artifact = {
        schema: KERNEL_CANVAS_ARTIFACT_SCHEMA,
        format_version: KERNEL_CANVAS_ARTIFACT_FORMAT_VERSION,
        objects: portableObjects,
        dependencies: edgeList(portableObjects),
    };
    assertValidKernelCanvasArtifact(artifact, { limits: artifactLimits(capabilities) });
    assertTenantViewPortableArtifactShape(artifact);
    const inventories = inventoryTenantViewArtifact(artifact, {
        inventorySecrets,
        inventoryIdentifiers,
    });
    const inventoried = { ...artifact, ...inventories };
    assertValidKernelCanvasArtifact(inventoried, { limits: artifactLimits(capabilities) });
    assertTenantViewPortableArtifactShape(inventoried);
    return inventoried;
}

function definitionForRestore(object) {
    const definition = textClone(object);
    delete definition.kind;
    delete definition.id;
    return definition;
}

function rewriteCanvasDependencies(canvas, idMap, commandMap) {
    const definition = definitionForRestore(canvas);
    definition.rows = rowsOf(definition).map((row) => ({
        ...row,
        cells: (row.cells || []).map((cell) => {
            const sourceId = cell.kernel_id ?? cell.kernelId;
            const targetId = idMap.get(sourceId);
            if (!targetId) throw new Error(`dependency ${sourceId} did not restore`);
            const command = commandMap.get(sourceId);
            if (!command) throw new Error(`dependency ${sourceId} is missing its command`);
            const next = { ...cell, kernel_id: targetId, command };
            delete next.kernelId;
            return next;
        }),
    }));
    delete definition.items;
    return definition;
}

async function restoreTenantViewArtifactAtomically(artifact, {
    importBundle,
    capabilities,
    cryptoImpl,
    bundledIds,
    secretOverrides,
}) {
    const formatVersion = Number(artifact.format_version);
    const objects = artifact.objects || [];
    const kernels = objects.filter((object) => object.kind === "kernel");
    const canvases = objects.filter((object) => object.kind === "canvas");
    const bundledSet = new Set(
        bundledIds instanceof Map ? bundledIds.keys() : bundledIds,
    );
    const idMap = new Map(
        [...bundledSet].map((id) => [id, portableTenantViewIdToWireId(id)]),
    );
    const commandMap = new Map(kernels.map((kernel) => [kernel.id, kernel.command]));
    const results = [];

    for (const object of objects) {
        if (object.kind === "kernel" && bundledSet.has(object.id)) {
            results.push({
                sourceId: object.id,
                kind: "kernel",
                status: "bundled",
                tenantViewId: null,
            });
            continue;
        }
        // Portable object UUIDs are already collision-resistant identities.
        // Reusing them as destinations makes server-side idempotency durable
        // without per-browser or per-object recovery records.
        idMap.set(object.id, portableTenantViewIdToWireId(object.id));
    }

    const importedObjects = objects
        .filter((object) => !(object.kind === "kernel" && bundledSet.has(object.id)))
        .map((object) => ({
            sourceId: object.id,
            destinationId: idMap.get(object.id),
            kind: object.kind,
            name: object.kind === "kernel" ? object.command : object.slug,
            title: object.title
                || (object.kind === "kernel" ? object.command : object.slug),
            formatVersion,
            definition: object.kind === "kernel"
                ? definitionForRestore(object)
                : rewriteCanvasDependencies(object, idMap, commandMap),
        }));
    if (importedObjects.length === 0) {
        return { results, idMap, complete: true, durable: true };
    }
    const rootSource = canvases.find((canvas) => idMap.has(canvas.id))
        || kernels.find((kernel) => idMap.has(kernel.id));
    const importRequestId = idMap.get(rootSource.id);
    try {
        const response = await importBundle({
            importRequestId,
            rootDestinationId: idMap.get(rootSource.id),
            objects: importedObjects,
            secretOverrides,
            cryptoImpl,
        }, { capabilities });
        if (!Array.isArray(response?.tenantViews)
            || response.tenantViews.length !== importedObjects.length) {
            throw new TypeError("bundle import response does not match its objects");
        }
        const importedResults = importedObjects.map((imported, index) => {
            const metadata = metadataOf(response.tenantViews[index]);
            if (metadata?.id !== imported.destinationId) {
                throw new TypeError("bundle import returned an unexpected destination id");
            }
            return {
                sourceId: imported.sourceId,
                kind: imported.kind,
                status: response.replayed ? "already_created" : "created",
                tenantViewId: metadata.id,
                version: metadata.version,
            };
        });
        results.push(...importedResults);
    } catch (error) {
        for (const imported of importedObjects) {
            results.push({
                sourceId: imported.sourceId,
                kind: imported.kind,
                status: "failed",
                error,
            });
        }
    }
    const complete = results.every((result) =>
        ["created", "already_created", "bundled"].includes(result.status));
    return {
        results,
        idMap,
        complete,
        durable: complete,
    };
}

/**
 * Restore/import uses one atomic server transaction when importBundle is
 * supplied. The createView fallback remains only for rollout skew with an
 * older backend and can be removed after that compatibility window.
 */
export async function restoreTenantViewArtifact(artifact, {
    createView,
    importBundle,
    capabilities = {},
    cryptoImpl = globalThis.crypto,
    bundledIds = builtinKernelIdMap,
    secretOverrides = [],
} = {}) {
    if (typeof importBundle !== "function" && typeof createView !== "function") {
        throw new TypeError("importBundle or createView is required");
    }
    assertValidKernelCanvasArtifact(artifact, { limits: artifactLimits(capabilities) });
    assertTenantViewPortableArtifactShape(artifact);
    if (typeof importBundle === "function") {
        return restoreTenantViewArtifactAtomically(artifact, {
            importBundle,
            capabilities,
            cryptoImpl,
            bundledIds,
            secretOverrides,
        });
    }

    const results = [];
    const bundledMap = bundledIds instanceof Map
        ? bundledIds
        : new Map([...bundledIds].map((id) => [id, id]));
    const idMap = new Map(bundledMap);
    const formatVersion = Number(artifact.format_version);
    const objects = artifact.objects || [];
    const kernels = objects.filter((object) => object.kind === "kernel");
    const canvases = objects.filter((object) => object.kind === "canvas");
    const commandMap = new Map(kernels.map((kernel) => [kernel.id, kernel.command]));

    for (const kernel of kernels) {
        if (bundledMap.has(kernel.id)) {
            results.push({ sourceId: kernel.id, kind: "kernel", status: "bundled", tenantViewId: null });
            continue;
        }
        try {
            const createRequestId = portableTenantViewIdToWireId(kernel.id);
            const created = await createView({
                kind: "kernel",
                name: kernel.command,
                title: kernel.title || kernel.command,
                formatVersion,
                definitionJson: JSON.stringify(definitionForRestore(kernel)),
                createRequestId,
            });
            const metadata = metadataOf(created);
            if (typeof metadata?.id !== "string" || !metadata.id) {
                throw new TypeError("create response is missing a tenant view id");
            }
            if (!["string", "number"].includes(typeof metadata.version)) {
                throw new TypeError("create response is missing a tenant view version");
            }
            idMap.set(kernel.id, metadata.id);
            const result = {
                sourceId: kernel.id,
                kind: "kernel",
                status: "created",
                tenantViewId: metadata.id,
                version: metadata.version,
            };
            results.push(result);
        } catch (error) {
            results.push({ sourceId: kernel.id, kind: "kernel", status: "failed", error });
        }
    }

    for (const canvas of canvases) {
        try {
            const createRequestId = portableTenantViewIdToWireId(canvas.id);
            const created = await createView({
                kind: "canvas",
                name: canvas.slug,
                title: canvas.title || canvas.slug,
                formatVersion,
                definitionJson: JSON.stringify(
                    rewriteCanvasDependencies(canvas, idMap, commandMap),
                ),
                createRequestId,
            });
            const metadata = metadataOf(created);
            if (typeof metadata?.id !== "string" || !metadata.id) {
                throw new TypeError("create response is missing a tenant view id");
            }
            if (!["string", "number"].includes(typeof metadata.version)) {
                throw new TypeError("create response is missing a tenant view version");
            }
            idMap.set(canvas.id, metadata.id);
            const result = {
                sourceId: canvas.id,
                kind: "canvas",
                status: "created",
                tenantViewId: metadata.id,
                version: metadata.version,
            };
            results.push(result);
        } catch (error) {
            results.push({ sourceId: canvas.id, kind: "canvas", status: "failed", error });
        }
    }

    const complete = results.every((result) =>
        ["created", "already_created", "bundled"].includes(result.status));
    return {
        results,
        idMap,
        complete,
        durable: complete,
    };
}
